// MED_BILLING — github.com/Simple-Life-Medical/med-billing-engine
// OpenAPI: github.com/Simple-Life-Medical/med-billing-engine/blob/develop/docs/openapi.yml
//
// The billing `customer_id` is derived server-side from the Auth0 token (Envoy rewrites it
// into X-Med-Authorization + X-Med-User-Id, which AuthMiddleware resolves via the user's
// med-users signup session), so the client never sends it. See docs/architecture/api.md.
import axios, { type AxiosInstance } from 'axios'
import { authedJson } from '@/api/http'
import { config } from '@/config'

const REPO = 'med-billing-engine' // Sentry `api` tag (placeholder until Sentry lands)
const BASE = `${config.medBillingHost}/med-billing-api`
const NONCE_ID = 'med-billing' // per-backend DPoP-nonce cache key

/**
 * Plain (unauthenticated) axios instance for the `@simple-life-app/web-billing` widget —
 * `PaymentClient` requires an AxiosInstance. Anonymous by design: on med-billing-engine
 * the payment-method-change trio (`POST /v1/client-session` → `POST /v1/payment-method/change`
 * → `POST /v1/payment/confirm`, plus `POST /v1/customers`) is registered WITHOUT
 * AuthMiddleware — the customer is identified by the `customer_id` UUID the widget puts in
 * each request body (same model as med-web-funnel's anonymous checkout). Only
 * `GET /v1/subscription` / `POST .../cancel` require the Auth0 DPoP transport (`authedJson`).
 */
export const billingWidgetAxios: AxiosInstance = axios.create({ baseURL: BASE })

export type SubscriptionProvider = 'palta' | 'stripe' | 'braintree' | 'primer' | 'flex' | 'paypal' | string
export type PhaseType = 'trial' | 'regular' | 'commitment' | string
export type IntervalUnit = 'week' | 'month' | 'year' | 'days' | string
export type SubscriptionStatus =
	| 'active'
	| 'awaiting_approval'
	| 'cancelled'
	| 'declined'
	| 'paused'
	| 'expired'
	| 'past_due'
	| string

/**
 * Clinical review state of the user's prescription, surfaced as the /status "Rx Status" row.
 *
 * Domain spelling, agreed with design (In review / Approved / Rejected). The wire spelling is
 * `RxStatusWire` (`review`, not `in_review`) and is translated by `mapRxStatus`; widen both,
 * don't replace them, if the backend adds states.
 */
export type RxStatus = 'in_review' | 'approved' | 'rejected'

/**
 * Wire spelling of the Rx state, as med-billing-engine derives it from the clinical order's
 * status (`get_subscription.go` → `rxStatusMap`: draft/processing/pending_* → `review`,
 * approved/fulfillment/completed → `approved`, rejected/cancelled → `rejected`).
 *
 * Shipped in [med-billing-engine#204](https://github.com/Simple-Life-Medical/med-billing-engine/pull/204)
 * (merged 2026-08-07). ⚠️ On `develop` → **med-dev only**; the latest release tag (`v0.22.0`,
 * cut from `main`) predates it, so on prod this field is absent and the "Rx Status" row hides.
 */
export type RxStatusWire = 'review' | 'approved' | 'rejected'

export interface IterationPrice {
	iteration: number
	price: number
	discount?: number | null
}

export interface Phase {
	phase_type: PhaseType
	is_active: boolean
	price: number
	currency?: string
	interval_unit?: IntervalUnit
	interval_duration?: number
	iteration_count?: number | null
	remain_iterations?: number | null
	cancellation_fee_amount?: number | null
	cancellation_fee_price_id?: string | null
	product_price_id?: string
	iteration_prices?: IterationPrice[]
}

// Domain shape consumed by the store and views. Produced from the wire DTO by
// `mapSubscriptionResponse`. Kept stable (ported from med-web-funnel) so the store
// getters, views, and analytics payloads don't churn when the backend shape changes.
export interface Subscription {
	subscription_id: string
	product_id: string
	/**
	 * Billing customer id (a UUID — equals the med-users signup-session id). Needed by the
	 * anonymous payment-method-change endpoints, where the widget sends it in request bodies.
	 */
	customer_id?: string
	provider: SubscriptionProvider
	status?: SubscriptionStatus
	payment_method?: string
	funding_type?: 'CREDIT' | string
	billing_period_number?: number
	cancelled?: boolean
	cancelled_at?: string | null
	is_paused?: boolean
	is_expired?: boolean
	is_overdue?: boolean
	is_pause_allowed?: boolean
	is_cancel_offer_allowed?: boolean
	current_period_start_at?: string | null
	current_period_end_at?: string | null
	/**
	 * End of the current commitment cycle, and the authoritative access horizon for a commitment
	 * soft cancel: med-billing-engine's `cancelAtCommitmentEnd` sets `expired_at =
	 * commitment_end_at + renewal grace`, not `current_period_end_at + grace`.
	 *
	 * `null` whenever the active phase is not a `commitment` (`phaseSummary` fills it only for
	 * `IsCommitmentPhase`), which is what lets `getAccessEndsAt` fall back to
	 * `current_period_end_at` with no phase check. Note it is **dropped by the store's
	 * scheduled-merge** — the backend's scheduled DTO builder never sets it — so a subscription
	 * with a scheduled sibling reads as non-commitment here; use `checkPeriodType.isCommitment`
	 * when the phase itself is the question.
	 */
	commitment_end_at?: string | null
	/**
	 * Paid periods of the commitment cycle still ahead of the current one — the backend's
	 * `CommitmentIterationsLeft`, already decremented for a `pending` / `awaiting_approval`
	 * period. `null` outside a commitment.
	 *
	 * This is the number that decides whether the backend turns our `soft` into
	 * `soft_commitment` (`> 0`) or a plain `soft` (`0`). Carried but not yet rendered: the
	 * "you will not be charged in the future" copy on `/cancel/done` is wrong for a wind-down
	 * and needs a design decision before this number can be put on screen (see docs/INDEX.md).
	 */
	remain_iterations?: number | null
	/** Last day of entitlement once cancelled (`null` while the plan runs open-ended). */
	expired_at?: string | null
	/**
	 * How the subscription was cancelled (`soft` / `hard` / `soft_commitment` / `supply_end`);
	 * `null` while it never was. After a `409` on a `supply_end` cancel this tells our own earlier
	 * request (`supply_end`) apart from an option that went away.
	 */
	cancellation_type: string | null
	/** Every way out the backend offers right now — see `CancellationOptions`. Always all five keys. */
	cancellation_options: CancellationOptions
	/** The term, its supplies and the next charge (med-billing-engine#319) — see `SupplySchedule`. */
	supply_schedule: SupplySchedule | null
	last_charge_at?: string | null
	last_charge_amount?: number | null
	/** Prescription clinical-review state, flattened from the DTO's `order` — see `RxStatus`. */
	rx_status?: RxStatus
	phases?: Phase[]
	scheduled?: Subscription | null
	// Computed/merged on the client in the store:
	trial?: Phase
	regular?: Phase
	commitment?: Phase
}

// Wire shape returned by med-billing-engine. Translated into `Subscription` by
// `mapSubscriptionResponse` before it reaches the store/views.
export interface SubscriptionResponseDTO {
	customer_id: string
	subscription_id: string
	product_id: string
	type?: string
	status?: SubscriptionStatus
	start_at?: string | null
	cancelled?: boolean
	cancelled_at?: string | null
	is_expired?: boolean
	expired_at?: string | null
	is_paused?: boolean
	is_overdue?: boolean
	is_pause_allowed?: boolean
	is_cancel_offer_allowed?: boolean
	is_cancel_fee_allowed?: boolean
	is_upgraded?: boolean
	has_cancel_offer?: boolean
	is_intro_active?: boolean
	paused_until_at?: string | null
	last_paused_at?: string | null
	current_period_start_at?: string | null
	current_period_end_at?: string | null
	commitment_end_at?: string | null
	billing_period_number?: number
	remain_iterations?: number | null
	payment_method?: string
	funding_type?: 'CREDIT' | string
	provider?: SubscriptionProvider
	/**
	 * The subscription's clinical order. Carries the Rx state only — the backend attaches it to
	 * the **top-level** DTO, never to `scheduled` (different builder), which is why the store's
	 * scheduled-merge has to preserve it.
	 */
	order?: OrderResponseDTO
	phases?: Phase[]
	scheduled?: SubscriptionResponseDTO | null
	cancellation_type?: string | null
	cancellation_options?: Partial<CancellationOptions> | null
	supply_schedule?: SupplyScheduleDTO | null
}

/** `SupplyScheduleDTO` (med-billing-engine#319) — mapped to `SupplySchedule`; arrays and `next_charge` may be absent. */
export interface SupplyScheduleDTO {
	term_type: string
	term_months: number
	supplies_total: number
	supplies_shipped: number
	supplies?: Supply[] | null
	next_charge?: NextCharge | null
}

/** `SubscriptionPaymentResponseDTO` — the `pay-fee` answer. */
interface SubscriptionPaymentResponseDTO {
	required_action?: RequiredAction | null
	subscription?: SubscriptionResponseDTO | null
}

/** `OrderResponseDTO` — the Rx-state carrier nested in `SubscriptionResponseDTO.order`. */
export interface OrderResponseDTO {
	rx_status?: RxStatusWire | string
}

export interface FetchSubscriptionParams {
	includeExpired?: boolean
}

/**
 * `cancellation_type` as med-billing-engine acts on it. Two values are sendable; the third
 * (`soft_commitment`) is **derived server-side from `soft`** and is never sent from here:
 *
 * - `soft` — the wind-down. `determineCancellationType` (`cancel_subscription.go`) upgrades it to
 *   `soft_commitment` when the subscription is `active` / `awaiting_approval`, its active phase is
 *   a `commitment`, and `remain_iterations > 0`; otherwise it stays a plain `soft`:
 *   - plain `soft` → `expired_at = current_period_end_at + renewal grace`;
 *   - `soft_commitment` → the status stays open so the owed iterations bill and ship, and
 *     `expired_at = commitment_end_at + renewal grace` (`cancelAtCommitmentEnd`).
 *
 *   Either way the clinical order is left alone, so a prescription still under review keeps going.
 *   `soft` is also the backend's default when the field is omitted.
 * - `hard` — `expired_at = now`, and `determineCancellationType` passes it through untouched, so on
 *   a commitment it **breaks the cycle immediately** (the owed iterations never bill) instead of
 *   winding it down. On a subscription in `awaiting_approval` the backend additionally voids the
 *   uncaptured payment, closes the order reminders and cancels the clinical order — i.e. it cancels
 *   the **prescription** along with the plan. This app sends `hard` on a commitment too; that is a
 *   deliberate owner decision, not an oversight — see ADR 0016.
 *
 * ⚠️ Both behaviours arrived in
 * [med-billing-engine#204](https://github.com/Simple-Life-Medical/med-billing-engine/pull/204)
 * (develop → med-dev, 2026-08-07). On a backend older than that (including the current prod
 * release `v0.22.0`) `hard` is coerced to `soft_commitment` and an `awaiting_approval`
 * subscription always takes the void-and-cancel-order path — so there the two choices below are
 * indistinguishable. Don't ship the scope screen to prod before that release lands.
 *
 * - `supply_end` — commitment only (DEV-35727): the exit fee is split over the remaining payments of
 *   the current 3-month supply (`cancellation_options.installments_fee` is the live offer), and the
 *   plan + access end with that supply. The backend answers 409 "cancellation at supply end is not
 *   offered" whenever the option is absent — `cancelSubscription` turns that into
 *   `CancellationNotOfferedError`.
 */
export type CancellationType = 'soft' | 'hard' | 'supply_end'

/**
 * Why the customer leaves — the codes of the one reason list shared with the Kova admin
 * (DEV-36293), in the order `/cancel/reasons` shows them. Sent as `exit_reason` on the exit calls
 * (`cancelSubscription`, `payCancellationFee`): **an array of every picked reason** (the survey is
 * multi-select), left out when none was picked. The same codes are the screen's analytics ids.
 *
 * ⚠️ Ahead of the backend (owner, 2026-10-14): med-billing-engine develop (ab790c6) has
 * `exit_reason` only on the internal (agent) cancel and refund, as **one** string of the old five
 * (`medical`, `too_expensive`, `changed_mind`, `shipment_problems`, `other`). The customer
 * `CancelSubscriptionRequestDTO` / `PaySubscriptionFeeRequestDTO` don't declare it, and the JSON
 * binder ignores unknown fields, so it is dropped until billing adds it — as an array of these
 * codes. Added as a single string, a request carrying the array would fail to bind (400).
 */
export const EXIT_REASONS = [
	'side_effects',
	'not_seeing_results',
	'found_other_provider',
	'prescription_or_shipment',
	'no_longer_needed',
	'difficulty_administering',
	'lack_of_care_support',
	'too_expensive',
	'other',
] as const

export type ExitReason = (typeof EXIT_REASONS)[number]

/** One scheduled charge of a `supply_end` exit fee. Money in major units. */
export interface FeeInstallment {
	amount: number
	date: string
}

/**
 * `CancellationOptionDTO` — one way out and what it costs (med-billing-engine#309). Money in major
 * units. Which fields are set depends on the option — see `CancellationOptions`.
 */
export interface CancellationOption {
	/** What we charge (`instant_fee`, `installments_fee`) or what the cycle still bills (`end_of_commitment`). */
	amount?: number
	/** What goes back (`upfront_refund`; `instant_fee` when the cycle paid past what shipped). */
	refund?: number
	currency?: string
	/** `installments_fee` only: the charges replacing the renewals of the current supply. */
	installments?: FeeInstallment[]
	/** `end_of_commitment` only: charges left in the cycle. */
	charges_left?: number
	/** The breakdown a fee was priced from: paid in the cycle (less refunds), months shipped, ladder rate per month. */
	paid?: number
	shipped_months?: number
	exit_rate?: number
	/** When access ends if this option is taken. */
	access_until?: string
}

/**
 * `cancellation_options` — on `GET /subscription` and on every cancel / pay-fee answer. `null` = not
 * offered right now; a button exists only for a non-null key. Rules per option, calls and errors:
 * docs/todo/cancellation-options-contract.md.
 *
 * - `instant_fee` → `pay-fee` (end today); after a `supply_end` cancel: the unpaid instalments, no breakdown.
 * - `installments_fee` → `cancel` `supply_end` (fee over the rest of the current supply).
 * - `upfront_refund` → `cancel` `supply_end` on an upfront plan (not used by this app).
 * - `end_of_period` / `end_of_commitment` → `cancel` `soft`; the engine picks which, never both.
 */
export interface CancellationOptions {
	instant_fee: CancellationOption | null
	installments_fee: CancellationOption | null
	upfront_refund: CancellationOption | null
	end_of_period: CancellationOption | null
	end_of_commitment: CancellationOption | null
}

/**
 * `supply_schedule.term_type` (med-billing-engine#319, DEV-36310): how the term is sold — a `commitment`
 * cycle, a period paid `upfront` whose unshipped supplies a ladder refunds, or a `regular` period (the
 * legacy 12w/24w plans included). The one place this app tells the plan kinds apart (ADR 0017).
 */
export type TermType = 'commitment' | 'upfront' | 'regular'

/** One supply of the term. `state` is the engine's timeline state (`SHIPPED_SUPPLY_STATES` are out of the pharmacy). */
export interface Supply {
	/** 1-based within the term. */
	number: number
	/**
	 * `delivery_failed`, `delivered`, `shipped`, `at_pharmacy`, `preparing`, `in_review`, `not_approved`,
	 * `awaiting_intake`, `cancelled`, `upcoming` or `no_record`.
	 */
	state: string
	/** The supply's place on the plan's grid (term start + one supply length per supply before it) — not a ship date. */
	start_at: string
	/** When the state happened; `null` while the supply is ahead. */
	at: string | null
}

/** The states in which a supply has left the pharmacy — the engine's `DeliveryState.Shipped()`. */
export const SHIPPED_SUPPLY_STATES: readonly string[] = ['shipped', 'delivered', 'delivery_failed']

/** `supply_schedule.next_charge`: the scheduled renewal (or its estimate before it exists), before tax. */
export interface NextCharge {
	at: string
	amount: number
	currency: string
}

/**
 * `supply_schedule` on every subscription answer: the current term — the whole cycle of a commitment,
 * else the current paid period — its supplies and the charge after it. `null` only before the first
 * period starts; always there for a commitment (owner, 2026-10-21: no fallback for its absence).
 */
export interface SupplySchedule {
	term_type: TermType | string
	/** The term's length in months: the commitment cycle, else the paid period (3 / 6 / 12 upfront, 1 monthly). */
	term_months: number
	supplies_total: number
	/** Supplies out of the pharmacy — the figure the upfront refund is priced from. */
	supplies_shipped: number
	supplies: Supply[]
	/** `null` once the plan stops billing, while it is paused, or after a cancel other than "Keep my plan". */
	next_charge: NextCharge | null
}

/** The option picked is no longer offered (a `409` on `supply_end`, a non-card `400` on `pay-fee`) — re-read and redraw. */
export class CancellationNotOfferedError extends Error {
	constructor(message: string) {
		super(message)
		this.name = 'CancellationNotOfferedError'
	}
}

/**
 * A `hard` cancel answered `409` "already cancelled": billing refuses it over an earlier cancel ("renewals
 * only" under review), so the plan and the prescription are still on unless it already ended `hard`.
 */
export class AlreadyCancelledError extends Error {
	constructor(message: string) {
		super(message)
		this.name = 'AlreadyCancelledError'
	}
}

/** `pay-fee` was declined by the card (`400 payment_failed`). */
export class PaymentDeclinedError extends Error {
	constructor(readonly reasonCode: string | undefined) {
		super('payment_failed')
		this.name = 'PaymentDeclinedError'
	}
}

/** Error envelope of med-billing-engine (`internal/api/error_handler.go`). */
interface ErrorBodyDTO {
	code?: number
	error_code?: string
	message?: string
	reason_code?: string
}

function mapCancellationOptions(dto: Partial<CancellationOptions> | null | undefined): CancellationOptions {
	return {
		instant_fee: dto?.instant_fee ?? null,
		installments_fee: dto?.installments_fee ?? null,
		upfront_refund: dto?.upfront_refund ?? null,
		end_of_period: dto?.end_of_period ?? null,
		end_of_commitment: dto?.end_of_commitment ?? null,
	}
}

/** 3DS / redirect step a `pay-fee` charge can return instead of settling (`RequiredActionResponseDTO`). */
export interface RequiredAction {
	name?: string
	client_token?: string
	redirect_url?: string
}

export interface PayFeeResult {
	/** The re-read subscription; `null` when the body carried none. */
	subscription: Subscription | null
	/** Present when the charge needs 3DS before it settles — the plan is NOT ended yet. */
	requiredAction: RequiredAction | null
}

export interface CancelSubscriptionOptions {
	/** Defaults to `soft` — see `CancellationType` for what each value does server-side. */
	cancellationType?: CancellationType
	/** Optional free-form actor tag. `cancel_reason` / `source` are set server-side. */
	actionBy?: string
	/** The reasons picked on `/cancel/reasons`, sent as `exit_reason`; omitted when empty. */
	exitReasons?: readonly ExitReason[]
}

export interface PayCancellationFeeOptions {
	/** As on `CancelSubscriptionOptions`. */
	exitReasons?: readonly ExitReason[]
}

const RX_STATUS_BY_WIRE: Record<RxStatusWire, RxStatus> = {
	review: 'in_review',
	approved: 'approved',
	rejected: 'rejected',
}

/**
 * Wire → domain Rx state. Anything unrecognised (including the field's absence on a backend
 * that predates med-billing-engine#204) resolves to `undefined`, which hides the "Rx Status"
 * row instead of guessing a state — the row must never claim a review outcome we didn't get.
 */
export function mapRxStatus(wire: string | undefined | null): RxStatus | undefined {
	if (!wire) return undefined
	return RX_STATUS_BY_WIRE[wire as RxStatusWire]
}

/** `supply_schedule` as the domain reads it: `null` when absent, the lists and the next charge never undefined. */
export function mapSupplySchedule(dto: SupplyScheduleDTO | null | undefined): SupplySchedule | null {
	if (!dto) return null
	return { ...dto, supplies: dto.supplies ?? [], next_charge: dto.next_charge ?? null }
}

/**
 * Translate the med-billing-engine wire shape into the domain `Subscription`.
 *
 * Mostly a passthrough now that the real backend returns the full shape. Period fields are
 * kept as-is: `current_period_end_at` (the next-charge/renewal date — the UI's "Charge Date"
 * on /status, plus the "ends on {date}" / "days left" on /cancel/confirm + /done) and
 * `current_period_start_at` (period start — passed through, not currently surfaced in the UI).
 * `status` drives the /status status label; `is_overdue` / `is_pause_allowed` /
 * `is_cancel_offer_allowed` / `funding_type` are real fields (passthrough).
 *
 * The DTO has no last-charge data, so `last_charge_*` stay undefined and the /status
 * "Last billed" line hides. Verified against `docs/openapi.yml` on 2026-07-30: no
 * customer-facing endpoint exposes a charge amount + date (only the internal
 * `/internal/v1/admin/transactions` admin API does), so this cannot be filled client-side —
 * the backend has to add `last_charge_at` / `last_charge_amount` to the DTO.
 *
 * `rx_status` is flattened out of the nested `order` object and translated to the domain
 * spelling by `mapRxStatus` — the one field that is not a passthrough.
 *
 * `commitment_end_at` / `remain_iterations` are passthroughs (normalised to `null`). They describe
 * the commitment cycle: the first is the access horizon a commitment **soft** cancel runs to
 * (rendered by `/cancel/confirm` + `/cancel/done` via the store's `getAccessEndsAt`), the second is
 * what makes the backend derive `soft_commitment` from our `soft` — see `CancellationType` and
 * ADR 0016.
 */
export function mapSubscriptionResponse(dto: SubscriptionResponseDTO): Subscription {
	return {
		subscription_id: dto.subscription_id,
		product_id: dto.product_id,
		customer_id: dto.customer_id,
		provider: dto.provider ?? '',
		status: dto.status,
		payment_method: dto.payment_method,
		funding_type: dto.funding_type,
		billing_period_number: dto.billing_period_number,
		cancelled: dto.cancelled,
		cancelled_at: dto.cancelled_at ?? null,
		is_paused: dto.is_paused,
		is_expired: dto.is_expired,
		is_overdue: dto.is_overdue,
		is_pause_allowed: dto.is_pause_allowed,
		is_cancel_offer_allowed: dto.is_cancel_offer_allowed,
		current_period_start_at: dto.current_period_start_at ?? null,
		current_period_end_at: dto.current_period_end_at ?? null,
		commitment_end_at: dto.commitment_end_at ?? null,
		remain_iterations: dto.remain_iterations ?? null,
		expired_at: dto.expired_at ?? null,
		cancellation_type: dto.cancellation_type ?? null,
		cancellation_options: mapCancellationOptions(dto.cancellation_options),
		supply_schedule: mapSupplySchedule(dto.supply_schedule),
		rx_status: mapRxStatus(dto.order?.rx_status),
		phases: dto.phases,
		scheduled: dto.scheduled ? mapSubscriptionResponse(dto.scheduled) : null,
	}
}

/**
 * Fetch the customer's subscription. Resolves `null` when the customer has **no subscription on
 * file** — the backend answers `404 NOT_FOUND` ("record not found"), an expected state (not every
 * account has billing), not a load failure. Like cancel's idempotent `409`, the `404` is passed to
 * `authedJson` as a `warnStatuses` entry (captured at `warning` and returned, not thrown) so
 * `/status` can render its "You don't have an active subscription" empty state instead of an error
 * card. Genuine failures (other non-2xx, transport errors) still throw.
 */
export async function getSubscription({
	includeExpired = false,
}: FetchSubscriptionParams = {}): Promise<Subscription | null> {
	const query = includeExpired ? '?include_expired=true' : ''
	const { status, data } = await authedJson<SubscriptionResponseDTO>({
		api: REPO,
		nonceId: NONCE_ID,
		method: 'GET',
		url: `${BASE}/v1/subscription${query}`,
		flow: 'subscription',
		step: 'get',
		warnStatuses: [404],
	})
	// No subscription record for this customer → null (the caller renders the empty state).
	if (status === 404) return null
	if (!data) throw new Error('subscription: empty response body')
	return mapSubscriptionResponse(data)
}

/**
 * Cancel a subscription. `cancellationType` decides whether the prescription goes with the plan
 * — see `CancellationType`.
 *
 * The endpoint is **idempotent**: on success the handler answers `200` with the re-read
 * `SubscriptionResponseDTO` (`CancelSubscription` → `c.JSON(http.StatusOK, response)`), and
 * `409 CONFLICT` ("subscription already cancelled") when it is already cancelled. A `409` — or a
 * `200` whose body we can't parse — resolves to `null`: there is no fresh subscription to merge,
 * so the store keeps the current one and flips the `cancelled` flag. Not on `supply_end`
 * (`CancellationNotOfferedError`) or `hard` (`AlreadyCancelledError`), where a `409` can mean the call
 * did nothing. Genuine failures (other non-2xx, transport errors) still throw.
 */
export async function cancelSubscription(
	subscriptionId: string,
	{ cancellationType = 'soft', actionBy, exitReasons }: CancelSubscriptionOptions = {},
): Promise<Subscription | null> {
	const body: Record<string, unknown> = { cancellation_type: cancellationType }
	if (actionBy) body.action_by = actionBy
	if (exitReasons?.length) body.exit_reason = [...exitReasons]
	const { status, data } = await authedJson<SubscriptionResponseDTO>({
		api: REPO,
		nonceId: NONCE_ID,
		method: 'POST',
		url: `${BASE}/v1/subscriptions/${subscriptionId}/cancel`,
		body,
		flow: 'subscription',
		step: 'cancel',
		// 409 = already cancelled — an expected, idempotent outcome, not an error. Return it
		// (captured at `warning`) instead of throwing so the caller can treat it as success.
		warnStatuses: [409],
	})
	// …except on `supply_end`, where a 409 is "cancellation at supply end is not offered": nothing
	// happened. (Our own earlier request answers the same — the caller re-reads to tell them apart.)
	if (status === 409 && cancellationType === 'supply_end') {
		throw new CancellationNotOfferedError((data as ErrorBodyDTO | undefined)?.message ?? 'not offered')
	}
	// …and on `hard`, which billing checks for an earlier cancel first: over a `soft` one nothing happened.
	// Only the store's re-read can tell that from our own earlier `hard`.
	if (status === 409 && cancellationType === 'hard') {
		throw new AlreadyCancelledError((data as ErrorBodyDTO | undefined)?.message ?? 'already cancelled')
	}
	// 409 (already cancelled) or a bodyless 200 → cancelled, with no fresh subscription to map.
	if (status === 409 || !data) return null
	return mapSubscriptionResponse(data)
}

/**
 * The pay-fee `400`s that mean the buy-out is not offered (med-billing-engine `pay_subscription_fee.go` @
 * 7440132): the plan is cancelled another way, has no commitment left, or no ladder to price it.
 */
const PAY_FEE_NOT_OFFERED: readonly string[] = [
	'cancellation type not allowed for fee payment',
	'subscription has no commitment to buy out',
	'cancel fee price not found',
]

/**
 * Pay the commitment exit fee now and end the plan ("Cancel and pay $X", DEV-35727).
 *
 * `POST /v1/subscriptions/{id}/pay-fee` charges the default payment method for the live
 * `cancellation_options.instant_fee` and, on success, cancels the plan server-side (after a
 * `supply_end` cancel it settles the remaining instalments instead and leaves access as it is). It is a
 * recurring charge of the saved card, so no 3DS step is expected; a `required_action` would mean the plan
 * is still active. A `400` throws:
 * `PaymentDeclinedError` for `payment_failed` (a declined card), `CancellationNotOfferedError` for the
 * refusals that mean nothing is left to buy out (`PAY_FEE_NOT_OFFERED` — re-read and redraw), and a plain
 * `Error` for any other (no payment method on file…): nothing was charged, the failure screen offers a card.
 *
 * The endpoint has no idempotency key — callers must guard against a double submit.
 */
export async function payCancellationFee(
	subscriptionId: string,
	{ exitReasons }: PayCancellationFeeOptions = {},
): Promise<PayFeeResult> {
	const body: Record<string, unknown> = { subscription_id: subscriptionId }
	if (exitReasons?.length) body.exit_reason = [...exitReasons]
	const { status, data } = await authedJson<SubscriptionPaymentResponseDTO & ErrorBodyDTO>({
		api: REPO,
		nonceId: NONCE_ID,
		method: 'POST',
		url: `${BASE}/v1/subscriptions/${subscriptionId}/pay-fee`,
		body,
		flow: 'subscription',
		step: 'pay_fee',
		// Both 400 families are expected outcomes: a declined card, or a buy-out no longer offered.
		warnStatuses: [400],
	})
	if (status === 400) {
		const message = data?.message
		if (message === 'payment_failed') throw new PaymentDeclinedError(data?.reason_code)
		if (message && PAY_FEE_NOT_OFFERED.includes(message)) throw new CancellationNotOfferedError(message)
		// Anything else (`payment method not found`, a request billing can't bind) is refused before a charge,
		// yet the buy-out stays offered: re-reading and redrawing the options would only loop back here.
		throw new Error(message ?? 'pay-fee refused')
	}
	return {
		subscription: data?.subscription ? mapSubscriptionResponse(data.subscription) : null,
		requiredAction: data?.required_action ?? null,
	}
}

/**
 * What the user chose to cancel on `/cancel/scope` (shown only while the Rx is under review).
 * The values are the response ids from the GLP Cancellation Funnel Logging spec — they travel to
 * analytics as `data.responses` **and** pick the `cancellation_type` below.
 */
export type CancellationScope = 'cancel_my_current_plan' | 'cancel_future_renewals_only'

/**
 * The `/cancel/scope` answer expressed as a `cancellation_type`.
 *
 * There is **no** endpoint that takes a cancellation *scope* — the two options are simply the two
 * values of the existing cancel call's `cancellation_type`, so the answer is carried to
 * `/cancel/confirm` in the survey store and spent there (see ADR 0014). An absent/unknown scope
 * means the user never saw the screen (Rx not under review) → `soft`, the safe default that
 * leaves the prescription alone.
 *
 * The mapping is unconditional on the plan's phase: a commitment gets `hard` here too, breaking the
 * cycle rather than winding it down (ADR 0016).
 */
export function cancellationTypeForScope(scope: CancellationScope | '' | undefined): CancellationType {
	return scope === 'cancel_my_current_plan' ? 'hard' : 'soft'
}
